import { useEffect, useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Empty } from '@cloudflare/kumo/components/empty';
import { Table } from '@cloudflare/kumo/components/table';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Input } from '@cloudflare/kumo/components/input';
import { Radio } from '@cloudflare/kumo/components/radio';
import { ArrowCounterClockwise, Trash, UserPlus } from '@phosphor-icons/react';
import { api } from '@/lib/api';

// 140組の通常募集終了後、運営が個別に招待した特定メールアドレスだけ、通常の申込ページから
// 例外的に追加申込できるようにする allowlist の管理画面。
// 出展者Noは登録時に「決め打ち」か「自動 (送信時点の MAX+1)」かを選ぶ。使用済みのエントリは
// 削除できない (監査用に残す)。

const YEAR = 2026;

interface AllowlistEntry {
  id: string;
  email: string;
  eventYear: number;
  forcedApplicationNumber: number | null;
  usedAt: string | null;
  createdAt: string;
  usedApplicationNumber: number | null;
  usedExhibitorName: string | null;
}

export default function ApplicationAllowlistPage() {
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [numberMode, setNumberMode] = useState<'auto' | 'forced'>('auto');
  const [forcedNumber, setForcedNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: AllowlistEntry[] }>(
        `/api/admin/registration-allowlist?year=${YEAR}`
      );
      setEntries(res.data.data ?? []);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const resetForm = () => {
    setEmail('');
    setNumberMode('auto');
    setForcedNumber('');
  };

  const submit = async () => {
    setError(null);
    setInfo(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setError('メールアドレスを入力してください');
      return;
    }
    let forcedApplicationNumber: number | null = null;
    if (numberMode === 'forced') {
      const n = parseInt(forcedNumber, 10);
      if (!Number.isFinite(n) || n <= 0) {
        setError('出展者番号は正の整数で入力してください');
        return;
      }
      forcedApplicationNumber = n;
    }

    setSubmitting(true);
    try {
      await api.post('/api/admin/registration-allowlist', {
        email: trimmedEmail,
        eventYear: YEAR,
        forcedApplicationNumber,
      });
      setInfo(`${trimmedEmail} を許可リストに追加しました`);
      resetForm();
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entry: AllowlistEntry) => {
    setError(null);
    setInfo(null);
    setDeletingId(entry.id);
    try {
      await api.delete(`/api/admin/registration-allowlist/${entry.id}`);
      setInfo(`${entry.email} を許可リストから削除しました`);
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-kumo-default">申込許可リスト</h1>
          <p className="text-sm text-kumo-muted">
            通常募集終了後、個別に招待した特定のメールアドレスだけ通常の申込ページから
            例外的に追加申込できるようにします。出展者番号は登録時に決め打ちか自動 (MAX+1) を選べます。
          </p>
        </div>
        <Button variant="ghost" icon={ArrowCounterClockwise} loading={loading} onClick={refresh}>
          再読込
        </Button>
      </div>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      <Surface className="p-4">
        <h2 className="mb-3 text-base font-semibold text-kumo-default">メールアドレスを追加</h2>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-kumo-default">メールアドレス</span>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="exhibitor@example.com"
              className="max-w-sm"
            />
          </div>

          <Radio.Group
            legend="出展者番号"
            appearance="card"
            value={numberMode}
            onValueChange={(v) => setNumberMode(v as 'auto' | 'forced')}
          >
            <Radio.Legend className="sr-only">出展者番号</Radio.Legend>
            <Radio.Item
              label="自動 (送信時点の MAX+1)"
              description="申込が送信された時点で採番する、通常の申込と同じロジック。"
              value="auto"
            />
            <Radio.Item
              label="決め打ち番号"
              description="この allowlist 経由の申込にだけ割り当てる出展者番号を指定します。"
              value="forced"
            />
          </Radio.Group>

          {numberMode === 'forced' && (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-kumo-default">決め打ち番号</span>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                value={forcedNumber}
                onChange={(e) => setForcedNumber(e.currentTarget.value)}
                className="w-28 font-mono"
              />
            </div>
          )}

          <div>
            <Button variant="primary" icon={UserPlus} loading={submitting} onClick={submit}>
              追加
            </Button>
          </div>
        </div>
      </Surface>

      <Surface>
        <div className="border-b border-kumo-border px-4 py-3">
          <h2 className="text-base font-semibold text-kumo-default">
            {YEAR} 年度 許可リスト ({entries.length})
          </h2>
        </div>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader />
          </div>
        ) : entries.length === 0 ? (
          <Empty title="登録されたメールアドレスはありません" />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>メールアドレス</Table.Head>
                <Table.Head>出展者番号</Table.Head>
                <Table.Head>状態</Table.Head>
                <Table.Head>登録日</Table.Head>
                <Table.Head></Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {entries.map((entry) => (
                <Table.Row key={entry.id}>
                  <Table.Cell>{entry.email}</Table.Cell>
                  <Table.Cell>
                    {entry.forcedApplicationNumber !== null ? (
                      <span className="font-mono">#{entry.forcedApplicationNumber}</span>
                    ) : (
                      <Badge variant="outline">自動 (MAX+1)</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {entry.usedAt ? (
                      <Badge variant="outline">
                        使用済み
                        {entry.usedApplicationNumber !== null
                          ? ` (#${entry.usedApplicationNumber} ${entry.usedExhibitorName ?? ''})`
                          : ''}
                      </Badge>
                    ) : (
                      <Badge variant="outline">未使用</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>{new Date(entry.createdAt).toLocaleString('ja-JP')}</Table.Cell>
                  <Table.Cell>
                    <Button
                      variant="ghost"
                      icon={Trash}
                      disabled={!!entry.usedAt}
                      loading={deletingId === entry.id}
                      onClick={() => remove(entry)}
                    >
                      削除
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>
    </div>
  );
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string } } }).response;
    return r?.data?.error ?? r?.data?.message ?? '処理に失敗しました';
  }
  return e instanceof Error ? e.message : '処理に失敗しました';
}
